/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { BusBookingService } from 'src/app/services/bus-booking/bus-booking.service';
import { Subscription, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators';
import {
  InfiniteScrollCustomEvent,
  ModalController,
  IonSearchbar,
} from '@ionic/angular';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { ApiErrorService } from 'src/app/services/api-error.service';
@Component({
  selector: 'bus-station-list',
  templateUrl: './bus-station-list.component.html',
  styleUrls: ['./bus-station-list.component.scss'],
})
export class BusStationListComponent implements OnInit, OnDestroy {
  @Input() type: string;
  @Input() closeFlag: boolean;
  busStationList: any;
  public noData: number;
  public page: number;
  public count: number;
  public currentCount: number;
  public searchTerm: string = '';
  public currentHeaderData: any;
  private busStationSubscription: Subscription;
  private searchSubscription: Subscription;
  private searchSubject = new Subject<string>();
  public hasError: boolean = false;
  private errorSubscription: Subscription;
  @ViewChild('busStationSearch', { static: false }) searchbar: IonSearchbar;

  // tslint:disable-next-line: max-line-length
  constructor(
    private busBookingService: BusBookingService,
    private modalController: ModalController,
    private analyticsService: AnalyticsService,
    private localStorageService: LocalStorageService,
    private apiErrorService: ApiErrorService
  ) {
    this.noData = 0;
    this.busStationList = [];
  }

  ngOnInit() {
    this.page = 0;
    this.count = 20;
    this.currentCount = 20;
    this.searchTerm = '';
    this.currentHeaderData = {
      modalSlotEnd: this.closeFlag ? false : true,
      marl20: true,
      title: 'Select Bus Station',
    };
    this.setupDebouncedSearch();
    this.getBusStations();

    this.errorSubscription = this.apiErrorService.value.subscribe((val) => {
      this.hasError = val;
    });
  }

  ionViewDidEnter(): void {
    try {
      this.searchbar?.setFocus();
    } catch (e) {}
  }

  setupDebouncedSearch() {
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map((searchTerm: string) => searchTerm.trim()),
        switchMap((searchTerm: string) => {
          this.searchTerm = searchTerm;
          this.busStationList = [];
          this.noData = 0;
          this.page = 0;
          this.count = 20;

          return this.busBookingService.searchBuses(
            this.searchTerm,
            this.page,
            this.count
          );
        })
      )
      .subscribe((resData: any) => {
        const receivedBusStationsList = resData?.content || [];
        if (receivedBusStationsList && receivedBusStationsList.length > 0) {
          this.currentCount = receivedBusStationsList.length;
          this.busStationList = receivedBusStationsList;
        } else {
          this.noData = 1;
          this.analyticsService.trackResponseError(
            {
              apiName: 'getAllBusStations',
              message: 'API responded with empty payload',
              statusCode: 200,
              servicetype: this.type?.toLowerCase(),
            },
            'Bus Station List - Search No Data'
          );
        }
      });
  }

  getBusStations() {
    this.busBookingService
      .searchBuses(this.searchTerm, this.page, this.count)
      .subscribe({
        next: (resData: any) => {
          const receivedBusStationsList = resData?.content || [];
          if (receivedBusStationsList && receivedBusStationsList.length > 0) {
            this.currentCount = receivedBusStationsList.length;
            this.busStationList = receivedBusStationsList;
          } else {
            this.noData = 1;
          }
        },
        error: (err) => {
          this.busStationList = [];
          this.noData = 1;
          this.analyticsService.trackResponseError(
            {
              message: err.message,
              statusCode: err.status,
              apiName: 'getAllBusStations',
            },
            {
              search_term: this.searchTerm,
              type: this.type?.toLowerCase(),
              page: this.page,
              count: this.count,
            }
          );
        },
      });
  }

  async selectBusStation(busStation) {
    this.analyticsService.trackUnified('station_selected', {
      flow: 'bus',
      outcome: 'success',
      legacy_event_name: 'Bus Station Selected',
      entity_id: busStation?.entityId,
      address: busStation?.address,
    });
    this.modalController.dismiss({
      busStation,
    });
  }

  closeModal() {
    this.modalController.dismiss();
    this.analyticsService.trackUnified('station_modal_closed', {
      flow: 'bus',
      outcome: 'abandon',
      legacy_event_name: 'button_clicked',
      component: 'bus-station-list-component',
      button: 'close-modal-button',
    });
  }

  filterBusStationList(event) {
    const searchTerm = event.detail.value;
    this.searchSubject.next(searchTerm);
  }

  ionViewDidLeave() {
    if (this.busStationSubscription) {
      this.busStationSubscription.unsubscribe();
    }

    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.busStationSubscription) {
      this.busStationSubscription.unsubscribe();
    }
  }

  filterBusStationListOnEnter(event) {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.searchSubject.next(searchTerm);
  }

  getNextPage(event) {
    if (this.currentCount < this.count) {
      (event as InfiniteScrollCustomEvent).target.complete();
      return;
    }
    this.page++;
    this.busBookingService
      .searchBuses(this.searchTerm, this.page, this.count)
      .subscribe((resData: any) => {
        const receivedBusStationsList = resData?.content || [];
        if (receivedBusStationsList && receivedBusStationsList.length > 0) {
          this.currentCount = receivedBusStationsList.length;
          this.busStationList = [
            ...this.busStationList,
            ...receivedBusStationsList,
          ];
        } else {
          this.noData = 1;
        }
        (event as InfiniteScrollCustomEvent).target.complete();
        if (this.currentCount < this.count) {
          event.target.disabled = true;
        } else {
          event.target.disabled = false;
        }
      });
  }
}
